/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React from "react";
import cn from "classnames";
import findKey from "lodash/findKey";
import isEqual from "lodash/isEqual";
import styles from "gui/widgets/widgets.module.css";
import { Center } from "gui/center/center";
import { ContainedButton } from "components/ButtonOld/ButtonOld";
import { FullDashboard } from "lib/entries/types";
import { Icon } from "gui/icon/icon";
import {
  Layout,
  Layouts,
  Responsive as ReactGridLayout,
  WidthProvider,
} from "react-grid-layout";
import { ParameterMerge } from "lib/parameters/types";
import { VisualItem } from "gui/visual/visual";
import {
  ParamWidget,
  TextWidget,
  VisualizationWidget,
} from "lib/widgets/types";
import { WidgetsEditButton } from "gui/widgets/edit";
import { isQueryVisual } from "lib/visuals/visuals";
import { useParameterMapping } from "lib/parameters/hooks";
import { useQueryResult } from "lib/results/useQueryResult";
import { useSessionShowWatermark } from "gui/session/session";
import { Param } from "gui/parameters/parameters";
import { uuid4 } from "@sentry/utils";
import { uniqueParam } from "lib/parameters/parameters";
import { VisualParam } from "lib/visuals/types";
import { parseOwner } from "../../lib/entries/entries";
import ResponsiveHeightContextProvider from "./responsiveness/ResponsiveHeightContextProvider";
import {
  createLayouts,
  parseBreakpoint,
  parseLayouts,
  widgetKey,
} from "./layouts";
import withResponsiveHeight from "./responsiveness/withResponsiveHeight";
import widgetConfig from "./responsiveness/widgetConfig";

interface Props {
  dashboard: FullDashboard;
  parameters: ParameterMerge;
  refresh: () => Promise<void>;
  apiKey?: string;
  onApplyParam?: () => void;
  openRunModal?: () => void;
  onLayout: (dashboard: FullDashboard) => void;
  edit: boolean;
  disabledParameters: boolean;
}

// Used in dashboard page and dashboard embed page (via WidgetsMemo below)
const Widgets: React.FC<Props> = (props) => {
  const { dashboard, edit } = props;

  const updatedDashboard = React.useMemo(() => {
    return {
      ...dashboard,
      param_widgets: normalizeParameterWidgets(dashboard, props.parameters),
    };
  }, [dashboard]);

  const [layouts, setLayouts] = React.useState<
    ReturnType<typeof createLayouts>
  >(createLayouts(updatedDashboard));

  const textWidgets = React.useMemo(() => {
    return getTextWidgets(updatedDashboard, props);
  }, [updatedDashboard, props]);

  const visualizationWidgets = React.useMemo(() => {
    return getVisualizationWidgets(updatedDashboard, props);
  }, [updatedDashboard, props]);

  const paramWidgets = React.useMemo(() => {
    return getParamWidgets(updatedDashboard, props);
  }, [updatedDashboard, props]);

  React.useEffect(() => {
    setLayouts(createLayouts(updatedDashboard));
  }, [updatedDashboard]);

  const onLayoutChange = (layouts: Layout[], all: Layouts) => {
    if (props.edit) {
      const b = parseBreakpoint(findKey(all, (l) => l === layouts) ?? "");
      b && props.onLayout(parseLayouts(updatedDashboard, b, layouts));
    }
  };

  if (
    !dashboard.text_widgets?.length &&
    !dashboard.visualization_widgets?.length
  ) {
    return <Center>This dashboard is empty.</Center>;
  }

  if (!layouts) {
    return null;
  }

  return (
    <div className={cn(styles.grid, edit && styles.edit)}>
      <ResponsiveHeightContextProvider edit={edit} setLayouts={setLayouts}>
        <ReactGridLayoutWrap
          onLayoutChange={onLayoutChange}
          layouts={layouts}
          breakpoints={widgetConfig.breakpoints}
          cols={{ sm: 1, lg: 6 }}
          containerPadding={[0, 0]}
          margin={[widgetConfig.margin, widgetConfig.margin]}
          rowHeight={widgetConfig.rowHeight}
          isResizable={edit}
          isDraggable={edit}
          isDroppable={edit}
          useCSSTransforms
          verticalCompact
        >
          {paramWidgets}
          {visualizationWidgets}
          {textWidgets}
        </ReactGridLayoutWrap>
      </ResponsiveHeightContextProvider>
    </div>
  );
};

// For performance, we only re-render the grid layout when we have to:
//
// * When the user navigates to a new dashboard page.
// * When the user toggles the dashboard edit mode on or off.
// * When a (visualization/ text/ param) widget is added or removed (that's the w.id check).
// * When a text widget is edited (that's the w.text check).
//
export const WidgetsMemo = React.memo(Widgets, (prev, next) => {
  return (
    prev.dashboard.slug === next.dashboard.slug &&
    prev.parameters === next.parameters &&
    prev.onLayout === next.onLayout &&
    prev.refresh === next.refresh &&
    prev.apiKey === next.apiKey &&
    prev.edit === next.edit &&
    prev.disabledParameters === next.disabledParameters &&
    prev.onApplyParam === next.onApplyParam &&
    // Ignore change to `openRunModal`, it will allways work the same way
    isEqual(
      prev.dashboard.text_widgets?.map((w) => [w.id, w.text]),
      next.dashboard.text_widgets?.map((w) => [w.id, w.text])
    ) &&
    isEqual(
      prev.dashboard.visualization_widgets?.map((w) => [w.id]),
      next.dashboard.visualization_widgets?.map((w) => [w.id])
    ) &&
    isEqual(
      prev.dashboard.param_widgets?.map((w) => [w.id]),
      next.dashboard.param_widgets?.map((w) => [w.id])
    )
  );
});

const getTextWidgets = (dashboard: FullDashboard, props: Props) => {
  return dashboard.text_widgets?.map((widget) => [
    <article key={widgetKey(widget)} className={styles.cell}>
      <TextWidgetsCell
        minUnitHeight={widget.options.position.sizeY}
        widgetId={widget.id}
        {...props}
        textWidget={widget}
      />
    </article>,
  ]);
};

const getVisualizationWidgets = (dashboard: FullDashboard, props: Props) => {
  return dashboard.visualization_widgets?.map((widget) => [
    <article key={widgetKey(widget)} className={styles.cell}>
      {dashboard.is_archived ? (
        <DisabledVisualizationWidgetsCell
          {...props}
          visualizationWidget={widget}
        />
      ) : (
        <VisualizationWidgetsCell {...props} visualizationWidget={widget} />
      )}
    </article>,
  ]);
};

const getParamWidgets = (dashboard: FullDashboard, props: Props) => {
  const params: JSX.Element[][] = [];
  if (props.parameters.state.defaults.length > 0) {
    props.parameters.state.defaults.map((parameter) => {
      const param = dashboard.param_widgets?.find(
        (w) => w.key === parameter.key && w.query_id === parameter.query_id
      );
      const key =
        param !== undefined ? widgetKey(param) : uniqueParam(parameter);

      params.push([
        <article key={key} className={styles.cell}>
          <Param
            {...props}
            parameter={parameter}
            onApply={props.onApplyParam}
            openRunModal={props.openRunModal}
            disabled={props.disabledParameters}
          />
        </article>,
      ]);
    });
  }
  return params;
};

const TextWidgetsCell = withResponsiveHeight(
  React.memo((props: Props & { textWidget: TextWidget }) => {
    const watermark = useSessionShowWatermark();

    const button =
      props.edit &&
      (props.dashboard.is_archived ? (
        <ContainedButton
          className={styles.editButton}
          disabled
          toolTipProps={{
            label: "Archived dashboards cannot be updated",
            color: "gray",
            hidden: false,
          }}
        >
          <Icon icon="edit" />
        </ContainedButton>
      ) : (
        <WidgetsEditButton
          dashboard={props.dashboard}
          textWidget={props.textWidget}
          refresh={props.refresh}
        />
      ));

    if (!props.textWidget.visualization) {
      return null;
    }

    return (
      <>
        <VisualItem
          dashboard={props.dashboard}
          visual={props.textWidget.visualization}
          watermark={watermark}
          edit={props.edit}
        />
        {button}
      </>
    );
  })
);

const VisualizationWidgetsCell: React.FC<
  Props & { visualizationWidget: VisualizationWidget }
> = (props) => {
  if (!props.visualizationWidget.visualization) {
    return null;
  }

  if (isQueryVisual(props.visualizationWidget.visualization)) {
    const queryId =
      props.visualizationWidget.visualization.query_details?.query_id;
    const parameters = useParameterMapping(props.parameters, queryId);
    const result = useQueryResult(queryId, parameters, {
      can_refresh: parameters.every((p) => p.value === p.default_value),
      apiKey: props.apiKey,
    });
    const watermark = useSessionShowWatermark();

    const button = props.edit && (
      <WidgetsEditButton
        dashboard={props.dashboard}
        visualizationWidget={props.visualizationWidget}
        refresh={props.refresh}
      />
    );

    return (
      <>
        <VisualItem
          visual={props.visualizationWidget.visualization}
          dashboard={props.dashboard}
          queryId={queryId}
          queryName={
            props.visualizationWidget.visualization.query_details?.name
          }
          queryDescription={
            props.visualizationWidget.visualization.query_details?.description
          }
          owner={
            props.visualizationWidget.visualization.query_details &&
            parseOwner(props.visualizationWidget.visualization.query_details)
          }
          result={result}
          watermark={watermark}
          edit={props.edit}
        />
        {button}
      </>
    );
  }
  return null;
};

const DisabledVisualizationWidgetsCell: React.FC<
  Props & { visualizationWidget: VisualizationWidget }
> = (props) => {
  if (!props.visualizationWidget.visualization) {
    return null;
  }

  if (isQueryVisual(props.visualizationWidget.visualization)) {
    const queryId =
      props.visualizationWidget.visualization.query_details?.query_id;
    const result = undefined;
    const watermark = useSessionShowWatermark();

    const button = props.edit && (
      <ContainedButton
        className={styles.editButton}
        disabled
        toolTipProps={{
          label: "Archived dashboards cannot be updated",
          color: "gray",
          hidden: false,
        }}
      >
        <Icon icon="edit" />
      </ContainedButton>
    );

    return (
      <>
        <VisualItem
          visual={props.visualizationWidget.visualization}
          dashboard={props.dashboard}
          queryId={queryId}
          queryName={
            props.visualizationWidget.visualization.query_details?.name
          }
          queryDescription={
            props.visualizationWidget.visualization.query_details?.description
          }
          owner={
            props.visualizationWidget.visualization.query_details &&
            parseOwner(props.visualizationWidget.visualization.query_details)
          }
          result={result}
          watermark={watermark}
          edit={props.edit}
        />
        {button}
      </>
    );
  }
  return null;
};

// For each visualization widget we're going to find if the visualization's query has parameters.
// If it does, we're adding them to the dashboard param widgets
function normalizeParameterWidgets(
  dashboard: FullDashboard,
  parameters: ParameterMerge
): ParamWidget[] {
  const defaultParamWidgets: ParamWidget[] = [];
  const paramWidgets: ParamWidget[] = [];
  dashboard.visualization_widgets?.forEach((vw) => {
    const viz = vw.visualization;
    if (isQueryVisual(viz)) {
      let query_id: number;
      if (viz.query_details !== undefined) {
        query_id = viz.query_details.query_id;
      } else {
        throw new Error("Query details not found");
      }

      return viz.query_details.parameters?.map((p) => {
        const sizeX = p.key.includes(" ") || p.key.length > 20 ? 2 : 1;
        const defaultWidget: ParamWidget = {
          id: uuid4(),
          key: p.key,
          value: p.value,
          visualization_widget_id: vw.id,
          query_id: query_id,
          dashboard_id: dashboard.id,
          visualization: {
            type: "param",
            key: p.key,
          } as VisualParam,
          options: {
            position: {
              row: 0,
              col: 0,
              sizeX: sizeX,
              sizeY: 1,
            },
          },
        };

        const param = dashboard.param_widgets?.find(
          (pw) =>
            pw.key === p.key &&
            pw.visualization_widget_id === vw.id &&
            pw.query_id === query_id
        );
        param
          ? paramWidgets.push(param)
          : defaultParamWidgets.push(defaultWidget);
      });
    }
  });
  setInitialParamWidgetsOptions(defaultParamWidgets, parameters).map((p) =>
    paramWidgets.push(p)
  );

  return paramWidgets;
}

const ReactGridLayoutWrap = WidthProvider(ReactGridLayout);

// For all the new param widgets, we're going to set its initial position.
// To make sure the parameters are aligned and that there aren't any gaps between them, we need to use the
// parameters.state, because that's where we know which params are going to be used in the dashboard.
function setInitialParamWidgetsOptions(
  paramWidgets: ParamWidget[],
  parameters: ParameterMerge
): ParamWidget[] {
  const updatedParamWidgets: ParamWidget[] = [];
  if (parameters.state.defaults.length > 0) {
    let colIndex = 0;

    parameters.state.defaults.map((parameter) => {
      const param = paramWidgets?.find(
        (w) => w.key === parameter.key && w.query_id === parameter.query_id
      );

      if (param) {
        if (colIndex >= 6) {
          // 6 is the number of grid columns
          colIndex = 0;
        }
        const sizeX = param.key.includes(" ") || param.key.length > 20 ? 2 : 1;
        updatedParamWidgets.push({
          ...param,
          options: {
            position: {
              row: 0, // Always pin the query params to the top of the grid by default
              col: colIndex,
              sizeX: sizeX,
              sizeY: 1,
            },
          },
        });
        colIndex += sizeX;
      }
    });
  }
  return updatedParamWidgets;
}
